package com.appsyde.shared.modules.team.data.source

import com.appsyde.contract.ErrorResponseDto
import com.appsyde.shared.modules.team.data.CreateInviteRequestDto
import com.appsyde.shared.modules.team.data.InviteListItemDto
import com.appsyde.shared.modules.team.data.UserListItemDto
import com.appsyde.shared.modules.team.data.CreateInviteResponseDto
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.accept
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.HttpStatusCode
import kotlinx.serialization.json.Json

private val json = Json { ignoreUnknownKeys = true }

class TeamRemoteDataSourceImpl(
    private val http: HttpClient,
    private val baseUrl: String
) : TeamRemoteDataSource {

    private val usersUrl = baseUrl.trimEnd('/') + "/users"
    private val invitesUrl = baseUrl.trimEnd('/') + "/invites"

    override suspend fun getUsers(): List<UserListItemDto> = runCatching {
        http.get(usersUrl) {
            accept(ContentType.Application.Json)
        }.body<List<UserListItemDto>>()
    }.getOrElse { emptyList() }

    override suspend fun getInvites(): List<InviteListItemDto> = runCatching {
        http.get(invitesUrl) {
            accept(ContentType.Application.Json)
        }.body<List<InviteListItemDto>>()
    }.getOrElse { emptyList() }

    override suspend fun createInvite(email: String): CreateInviteResult = runCatching {
        val response = http.post(invitesUrl) {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(CreateInviteRequestDto(email = email))
        }
        when (response.status) {
            HttpStatusCode.OK -> {
                val body = response.body<CreateInviteResponseDto>()
                CreateInviteResult.Ok(body.inviteId)
            }
            HttpStatusCode.BadRequest -> {
                val err = runCatching { json.decodeFromString<ErrorResponseDto>(response.bodyAsText()) }.getOrNull()
                when (err?.code) {
                    "invalid_email" -> CreateInviteResult.InvalidEmail
                    "cannot_invite_owner" -> CreateInviteResult.CannotInviteOwner
                    else -> CreateInviteResult.Error(err?.message ?: "Invalid request")
                }
            }
            HttpStatusCode.Conflict -> CreateInviteResult.AlreadyMember
            else -> CreateInviteResult.Error(response.status.description)
        }
    }.getOrElse { e -> CreateInviteResult.Error(e.message) }

    override suspend fun revokeInvite(inviteId: String): Boolean = runCatching {
        val response = http.post("$invitesUrl/$inviteId/revoke") {
            accept(ContentType.Application.Json)
        }
        response.status == HttpStatusCode.OK
    }.getOrElse { false }

    override suspend fun disableUser(userId: String): Boolean = runCatching {
        val response = http.post("$usersUrl/$userId/disable") {
            accept(ContentType.Application.Json)
        }
        response.status == HttpStatusCode.OK
    }.getOrElse { false }
}
